import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  WarningIcon,
  XCircleIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";

/* ---------------------------------------------------------------- surfaces */

export function Panel({
  title,
  action,
  children,
  flush,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={className ? `panel ${className}` : "panel"}>
      {title && (
        <header className="panel__head">
          <h2 className="panel__title">{title}</h2>
          {action}
        </header>
      )}
      <div className={flush ? "panel__body panel__body--flush" : "panel__body"}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ badges */

export type Tone = "ok" | "warn" | "bad" | "neutral" | "accent";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="badge" data-tone={tone}>
      <span className="badge__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- buttons */

export function Button({
  variant = "ghost",
  size,
  block,
  loading,
  icon,
  children,
  ...rest
}: {
  variant?: "primary" | "ghost" | "quiet" | "danger";
  size?: "sm";
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const className = [
    "btn",
    `btn--${variant}`,
    size === "sm" ? "btn--sm" : "",
    block ? "btn--block" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={className} disabled={loading || rest.disabled} {...rest}>
      {loading ? (
        <SpinnerGapIcon size={15} weight="bold" className="spin" aria-hidden="true" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ states */

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__icon" aria-hidden="true">
        {icon}
      </div>
      <p className="empty__title">{title}</p>
      <p className="empty__body">{body}</p>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <EmptyState
      icon={<XCircleIcon size={22} />}
      title="Could not load this"
      body={message}
      action={
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

/** Skeletons mirror the real row shape so nothing shifts when data lands. */
export function RowSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="records" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div className="record" key={index}>
          <div>
            <div className="skel" style={{ height: 15, width: `${52 + ((index * 13) % 30)}%` }} />
            <div className="skel" style={{ height: 12, width: "38%", marginTop: 7 }} />
          </div>
          <div className="skel" style={{ height: 13, width: "72%" }} />
          <div className="skel" style={{ height: 13, width: "48%" }} />
          <div className="skel" style={{ height: 26, width: 76, borderRadius: 999 }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton → content cross-fade (transitions-dev `14-skeleton-reveal`).
 *
 * The two layers share one grid cell, so the panel keeps the height of
 * whichever is taller and nothing jumps when the data lands. The skeleton is
 * unmounted once the reveal has finished, otherwise a short result set would
 * keep the taller skeleton's height forever.
 *
 * `--reveal-dur` is read from the stylesheet rather than duplicated here, so
 * retuning the token cannot desynchronise the unmount from the fade.
 */
export function SkeletonReveal({
  loading,
  skeleton,
  children,
}: {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}) {
  const [showSkeleton, setShowSkeleton] = useState(loading);

  useEffect(() => {
    if (loading) {
      setShowSkeleton(true);
      return;
    }
    if (!showSkeleton) return;
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--reveal-dur");
    const ms = Number.parseFloat(raw) || 400;
    const timer = setTimeout(() => setShowSkeleton(false), ms);
    return () => clearTimeout(timer);
  }, [loading, showSkeleton]);

  return (
    <div className={loading ? "t-skel" : "t-skel is-revealed"}>
      {showSkeleton && (
        <div className="t-skel-skeleton" aria-hidden="true">
          {skeleton}
        </div>
      )}
      <div className="t-skel-content">{children}</div>
    </div>
  );
}

/**
 * Segmented control with a sliding pill (transitions-dev `16-tabs-sliding`).
 *
 * JS only measures and writes the active segment's position; CSS owns the
 * tween. The first paint and any resize are written with the transition
 * suspended, so the pill never animates in from zero width.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
  label: string;
}) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const firstPaint = useRef(true);

  const move = useCallback((animate: boolean) => {
    const pill = pillRef.current;
    const tab = barRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    if (!pill || !tab) return;

    if (!animate) {
      const previous = pill.style.transition;
      pill.style.transition = "none";
      pill.style.transform = `translateX(${tab.offsetLeft}px)`;
      pill.style.width = `${tab.offsetWidth}px`;
      void pill.offsetWidth;
      pill.style.transition = previous;
    } else {
      pill.style.transform = `translateX(${tab.offsetLeft}px)`;
      pill.style.width = `${tab.offsetWidth}px`;
    }
  }, []);

  useLayoutEffect(() => {
    move(!firstPaint.current);
    firstPaint.current = false;
  }, [value, move]);

  useEffect(() => {
    const onResize = () => move(false);
    window.addEventListener("resize", onResize);
    // The variable font lands after first paint and changes every label's
    // width; without this the pill sits half a word wide until the first click.
    void document.fonts?.ready.then(() => move(false)).catch(() => {});
    return () => window.removeEventListener("resize", onResize);
  }, [move]);

  return (
    <div className="t-tabs" role="tablist" aria-label={label} ref={barRef}>
      <span className="t-tabs-pill" aria-hidden="true" ref={pillRef} />
      {options.map((option) => (
        <button
          key={option.value}
          className="t-tab"
          role="tab"
          type="button"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A figure that re-enters when it changes (transitions-dev `02-number-pop-in`).
 *
 * Deliberately silent on first paint: an entrance every time the screen mounts
 * is the page-load choreography DESIGN.md removed. It animates only when the
 * value actually changes underneath the reader — which, on a live dashboard,
 * is the moment worth noticing.
 */
export function PopNumber({ value }: { value: string }) {
  const groupRef = useRef<HTMLSpanElement | null>(null);
  const previous = useRef<string | null>(null);

  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) return;

    if (previous.current === null || previous.current === value) {
      previous.current = value;
      return;
    }
    previous.current = value;

    // Remove, reflow, re-add: without the forced reflow the browser coalesces
    // the two class writes and the animation never replays.
    group.classList.remove("is-animating");
    void group.offsetHeight;
    group.classList.add("is-animating");
  }, [value]);

  const characters = [...value];

  return (
    <span className="t-digit-group" ref={groupRef}>
      {characters.map((character, index) => (
        <span
          className="t-digit"
          key={`${index}-${character}`}
          data-stagger={
            index === characters.length - 2
              ? "1"
              : index === characters.length - 1
                ? "2"
                : undefined
          }
        >
          {character}
        </span>
      ))}
    </span>
  );
}

/** The chevron the accordion flips, and the one the selects wear. Symmetric
 *  about the viewBox centre so `scaleY(-1)` lands exactly on the "^". */
export function Chevron({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6.5L8 10.5L12 6.5" />
    </svg>
  );
}

/**
 * Hover/focus tooltip (transitions-dev `17-tooltip`). Pure CSS; this only
 * wires the documented hooks and the `aria-describedby` link.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const id = useId();
  return (
    <span className="t-tt-wrap">
      <span className="t-tt-trigger" aria-describedby={id}>
        {children}
      </span>
      <span className="t-tt" id={id} role="tooltip">
        {text}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ paging */

export function Pager({
  page,
  totalPages,
  totalItems,
  perPage,
  busy,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalItems: number;
  perPage: number;
  busy?: boolean;
  onChange: (next: number) => void;
}) {
  const from = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalItems);

  return (
    <div className="pager">
      <p className="pager__status">
        {totalItems === 0 ? (
          "Nothing to show"
        ) : (
          <>
            <span className="mono">
              {from}&ndash;{to}
            </span>{" "}
            of <span className="mono">{totalItems}</span>
          </>
        )}
      </p>
      <div className="row row--2">
        <Button
          variant="ghost"
          size="sm"
          icon={<CaretLeftIcon size={13} weight="bold" />}
          disabled={busy || page <= 1}
          onClick={() => onChange(page - 1)}
          aria-label="Previous page"
        >
          Previous
        </Button>
        <span className="pager__status mono" aria-live="polite">
          {page} / {Math.max(1, totalPages)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || page >= totalPages}
          onClick={() => onChange(page + 1)}
          aria-label="Next page"
        >
          Next
          <CaretRightIcon size={13} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ toasts */

export interface Toast {
  id: number;
  tone: "ok" | "bad";
  text: string;
  undo?: () => void;
}

export function Toaster({ toasts, onUndo }: { toasts: Toast[]; onUndo: (toast: Toast) => void }) {
  return (
    <div className="toasts" role="status" aria-live="polite">
      {/* A toast is the confirmation that an action landed, so it earns motion:
          it rises in and drops out. Same spring the row lists use — no bounce,
          and `layout` keeps a stack of them from jumping when one expires. */}
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <motion.div
            className="toast"
            data-tone={toast.tone}
            key={toast.id}
            layout
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ type: "spring", duration: 0.25, bounce: 0 }}
          >
            <span className="toast__icon" aria-hidden="true">
              {toast.tone === "ok" ? (
                <CheckCircleIcon size={17} weight="fill" />
              ) : (
                <WarningIcon size={17} weight="fill" />
              )}
            </span>
            <span className="toast__text">{toast.text}</span>
            {toast.undo && (
              <button className="toast__undo" onClick={() => onUndo(toast)}>
                Undo
              </button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
